import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { RouteError } from "./components/layout/RouteError";
import { currentPersonQueryOptions } from "./hooks/use-current-person";
import { PTO_EMAIL_ACTIONS, type PtoEmailAction } from "./lib/enums";
import { queryClient } from "./lib/query";
import { isSafeReturnPath } from "./lib/return-path";
import {
  redirectHome,
  redirectIfSignedIn,
  requireAdmin,
  requireAuth,
  requirePerson,
} from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { ExpensesPage } from "./routes/dashboard/expenses";
import { GuidancePage } from "./routes/dashboard/guidance";
import { IncentivesPage } from "./routes/dashboard/incentives";
import { DashboardPage } from "./routes/dashboard/index";
import { ProfilePage } from "./routes/dashboard/profile";
import { PtoPage } from "./routes/dashboard/pto";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsJobsPage } from "./routes/dashboard/settings/jobs";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { TeamPage } from "./routes/dashboard/team";
import { TimePage } from "./routes/dashboard/time";
import { LoginPage } from "./routes/login";
import { NoAccessPage } from "./routes/no-access";
import { SignupPage } from "./routes/signup";
import { profileSearchSchema } from "./schemas/calendar";
import { incentivesSearchSchema } from "./schemas/incentives";
import { jobsSearchSchema } from "./schemas/jobs";
import { monthSearchSchema } from "./schemas/month";
import { teamSearchSchema } from "./schemas/team";
import { timeSearchSchema } from "./schemas/time";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => redirectHome(),
});

// Better Auth sends a failed Microsoft sign-in back here as ?error=<code> (src/lib/sign-in-errors.ts).
// ?redirect= is where to go after sign-in; anything but a safe path on this site is dropped.
const loginSearchSchema = z.object({
  error: z.string().optional(),
  redirect: z.string().refine(isSafeReturnPath).optional().catch(undefined),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>) => loginSearchSchema.parse(search),
  beforeLoad: ({ search }) => redirectIfSignedIn(search.redirect),
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/no-access",
  beforeLoad: () => requireAuth(),
  component: NoAccessPage,
});

function DashboardLayoutRoute() {
  const { signedIn } = dashboardLayout.useRouteContext();
  return (
    <DashboardLayout user={signedIn}>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: async ({ location }) => {
    const session = await requireAuth(location);
    await requirePerson(session.user.role, location);
    return { signedIn: session.user };
  },
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  validateSearch: (search: Record<string, unknown>) => monthSearchSchema.parse(search),
  component: DashboardPage,
});

// ?week= (a Sunday), ?day= (the Today chip), ?view=entries, ?log|favorites|signoff=open
// (src/schemas/time.ts).
const timeRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/time",
  validateSearch: (search: Record<string, unknown>) => timeSearchSchema.parse(search),
  component: TimePage,
});

const guidanceRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/guidance",
  validateSearch: (search: Record<string, unknown>) => monthSearchSchema.parse(search),
  component: GuidancePage,
});

const incentivesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/incentives",
  validateSearch: (search: Record<string, unknown>) => incentivesSearchSchema.parse(search),
  component: IncentivesPage,
});

const teamRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/team",
  validateSearch: (search: Record<string, unknown>) => teamSearchSchema.parse(search),
  // Managers only (the nav hides it too); everyone else goes Home.
  beforeLoad: async () => {
    const me = await queryClient.ensureQueryData(currentPersonQueryOptions);
    if (!me.managesAnyone) throw redirect({ to: "/dashboard" });
  },
  component: TeamPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  // ?rule=new (Add rule dialog), ?calendar=error (a failed Connect calendar).
  validateSearch: (search: Record<string, unknown>) => profileSearchSchema.parse(search),
  component: ProfilePage,
});

// ?year=, ?tab=mine|approvals, ?request=new (opens the request dialog, Home's PTO request),
// ?decide=<request id>&action=approve|reject (an approver's email button: that step's dialog).
const ptoRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/pto",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    year?: number;
    tab?: "mine" | "approvals";
    request?: "new";
    decide?: string;
    action?: PtoEmailAction;
  } => {
    const y = Number(search.year);
    const decide = typeof search.decide === "string" ? search.decide : "";
    const action = PTO_EMAIL_ACTIONS.find((a) => a === search.action);
    const valid = /^[0-9A-Fa-f-]{36}$/.test(decide) && action !== undefined;
    return {
      year: Number.isInteger(y) && y >= 2020 && y <= 2100 ? y : undefined,
      tab: search.tab === "approvals" || search.tab === "mine" ? search.tab : undefined,
      request: search.request === "new" ? "new" : undefined,
      decide: valid ? decide : undefined,
      action: valid ? action : undefined,
    };
  },
  component: PtoPage,
});

// ?year=, ?tab=mine|approvals, ?expense=new (opens the New expense form, Home's Expense),
// ?claim=<id> (opens that claim).
const expensesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/expenses",
  validateSearch: (
    search: Record<string, unknown>,
  ): { year?: number; tab?: "mine" | "approvals"; expense?: "new"; claim?: string } => {
    const y = Number(search.year);
    const claim = typeof search.claim === "string" ? search.claim : "";
    return {
      year: Number.isInteger(y) && y >= 2020 && y <= 2100 ? y : undefined,
      tab: search.tab === "approvals" || search.tab === "mine" ? search.tab : undefined,
      expense: search.expense === "new" ? "new" : undefined,
      claim: /^[0-9A-Fa-f-]{36}$/.test(claim) ? claim : undefined,
    };
  },
  component: ExpensesPage,
});

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: Outlet,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  // ?template=<key> opens that email template's edit page.
  validateSearch: (search: Record<string, unknown>): { template?: string } => ({
    template: typeof search.template === "string" && search.template ? search.template : undefined,
  }),
  component: SettingsEmailPage,
});

// ?view=history opens the run history tab.
const settingsJobsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/jobs",
  validateSearch: (search: Record<string, unknown>) => jobsSearchSchema.parse(search),
  component: SettingsJobsPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: SettingsAuditPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  noAccessRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    timeRoute,
    guidanceRoute,
    teamRoute,
    ptoRoute,
    expensesRoute,
    incentivesRoute,
    profileRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsAuditRoute,
      settingsJobsRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree, defaultErrorComponent: RouteError });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
